import Clients from '../collections/Clients';
import Client from '../models/Client';
import { baseApi } from 'core/api';

const state = () => ({
    _items: null,
    _itemsAll: null,
    _itemsAllLoading: false,
    _isItemsAllLoaded: false,
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: null,
        length: 10,
        fields_list: [
            'email',
            'plan_type',
            'brand_status',
            'campaign_status',
            'subscription_status',
            'available_credits',
        ],
    },
    _editing: null,
    _deleting: null,
    _mappingToTeams: null,
    _reMappingToTeams: null,
    _deletingTeamMapping: null,
    _groupsTeams: [],
    _groupsTeamsLoading: false,
});

const getters = {
    clientsAll: (state) => state._itemsAll,
    clientsAllLoaded: (state) => state._isItemsAllLoaded,
    clientsAllLoading: (state) => state._itemsAllLoading,
    clients: (state) => state._items,
    clientEditing: (state) => state._editing,
    clientDeleting: (state) => state._deleting,
    clientMappingToTeams: (state) => state._mappingToTeams,
    clientReMappingToTeams: (state) => state._reMappingToTeams,
    clientDeletingTeamMapping: (state) => state._deletingTeamMapping,
    clientGroupsTeams: (state) => state._groupsTeams,
    clientGroupsTeamsLoading: (state) => state._groupsTeamsLoading,
    clientsParameters: (state) => state._parameters,
    clientsPagination: (state) => state._pagination,
};

const actions = {
    loadClient(id) {
        return baseApi
            .get(`core/agency/clients/${id}`)
            .then((response) => {
                this._setClientLoaded(response.data);
                return response;
            })
            .catch(() => {
                this._setClientLoadError();
            });
    },

    loadClients(retry) {
        if (retry === true) {
            this._setClientsLoadRetry();
        }

        return baseApi
            .get('core/agency/clients', {
                params: this._parameters,
            })
            .then((response) => {
                this._setClientsLoaded(response.data);

                return response.data;
            })
            .catch(() => {
                this._setClientsLoadError();
            });
    },

    loadAllClients() {
        this._setAllClientsLoading(true);
        return baseApi
            .get('core/agency/clients', {
                params: { all: true },
            })
            .then((response) => {
                this._setAllClientsLoaded(response.data);
                this._setAllClientsLoading(false);

                return response.data;
            })
            .catch(() => {
                this._setAllClientsLoadError();
            });
    },

    deleteClient({ id, form }) {
        return form.delete(`core/agency/clients/${id}`).then(() => {
            this.loadClients();
        });
    },

    mapClientToTeams({ id, form }) {
        return form.put(`core/groups/map-client/${id}`).then(() => {
            this.loadClients();
        });
    },

    deleteClientTeamMapping({ id, form }) {
        return form.delete(`core/groups/client-mapping/${id}`).then(() => {
            this.loadClients();
        });
    },

    setClientsUpdateSort(sort) {
        this._setClientsUpdateSort(sort);

        return this.loadClients();
    },

    setClientsUpdateSearch(search) {
        this._setClientsUpdateSearch(search);

        return this.loadClients();
    },

    setClientsClearSearch() {
        this._setClientsSearchCleared();
        return this.loadClients();
    },

    setClientsUpdatePage(page) {
        this._setClientsUpdatePage(page);

        return this.loadClients();
    },

    clientsUpdateLength(page) {
        this._setClientsUpdateLength(page);

        return this.loadClients();
    },

    setClientEditing(client) {
        this._setClientEditing(client);
    },

    setClientDeleting(client) {
        this._setClientDeleting(client);
    },

    setClientUpdated(client) {
        client = client instanceof Client ? client : new Client(client);

        this._setClientUpdated(client);
    },

    setClientMappingToTeams(client) {
        this._setClientMappingToTeams(client);
    },

    setClientReMappingToTeams(client) {
        this._setClientReMappingToTeams(client);
    },

    setClientDeletingTeamMapping(client) {
        this._setClientDeletingTeamMapping(client);
    },

    getGroupsIntegrationTeams({ update = true } = {}) {
        if (update) {
            if (this._groupsTeamsLoading) return;

            this._setClientGroupsIntegrationTeamsLoading(true);
        }

        return baseApi
            .get('core/groups/integration-teams')
            .then((response) => {
                if (update) {
                    this._setClientGroupsIntegrationTeams(response.data);
                }

                return response.data;
            })
            .finally(() => {
                if (update) {
                    this._setClientGroupsIntegrationTeamsLoading(false);
                }
            });
    },

    getGroupsRefreshIntegrationTeams({ params = {} }) {
        return baseApi
            .get('core/groups/refresh-integration-teams', { params: params })
            .then((response) => {
                return response;
            });
    },

    getGroupsRefreshIntegrationTeamsContactsById({ id }) {
        return baseApi
            .get(`core/groups/refresh-integration-teams-contacts/${id}`)
            .then((response) => {
                return response;
            });
    },

    _setUserMeSignal() {
        this.client = null;
    },

    // Private methods
    _setClientReMappingToTeams(client) {
        this._reMappingToTeams = client;
    },

    _setClientGroupsIntegrationTeamsLoading(status) {
        this._groupsTeamsLoading = status;
    },

    _setClientGroupsIntegrationTeams(teams) {
        this._groupsTeams = teams;
    },

    _setClientLoaded(data) {
        this.client = data;
    },

    _setClientsLoadRetry() {
        this._items = null;
    },

    _setClientsLoaded(data) {
        this._items = new Clients(data.data).models;
        this._pagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        };
    },

    _setClientsLoadError() {
        this._items = [];
    },

    _setAllClientsLoaded(data) {
        this._itemsAll = new Clients(data).models;
        this._isItemsAllLoaded = true;
    },

    _setAllClientsLoading(status) {
        this._itemsAllLoading = status;
    },

    _setAllClientsLoadError() {
        // Handle error case
    },

    _setClientLoadError() {
        // Handle error case
    },

    _setClientUpdated(data) {
        if (!this._items || !Array.isArray(this._items)) {
            return;
        }

        let index = this._items.findIndex((o) => o.id === data.id);

        if (index === -1) {
            return;
        }

        const existingClient = this._items[index];

        if (existingClient.groups && existingClient.groups.length > 0) {
            if (!data.groups || data.groups.length === 0) {
                data.groups = [...existingClient.groups];
            }
        }

        this._items.splice(index, 1, data);
    },

    _setClientsUpdateSort(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
    },

    _setClientsUpdateSearch(data) {
        this._parameters.search = data;
    },

    _setClientsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setClientsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setClientsSearchCleared() {
        this._parameters.search = null;
    },

    _setClientEditing(data) {
        this._deleting = null;
        this._editing = data;
    },

    _setClientDeleting(data) {
        this._deleting = data;
    },

    _setClientMappingToTeams(data) {
        this._mappingToTeams = data;
    },

    _setClientDeletingTeamMapping(data) {
        this._deletingTeamMapping = data;
    },
};

export default {
    state,
    getters,
    actions,
};
